/**
 * Backtest a v2 model against a year it already knows the answer for.
 *
 * The notes from the server are shown ABOVE the numbers, not below them. The
 * prediction is seeded by carrying the prior year forward, which is a naive
 * baseline — a large revenue error is expected and says nothing about the
 * model. A user who reads the error before the caveat will draw the wrong
 * conclusion, and most users read top to bottom.
 */

import { useEffect, useState } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { Markdown } from '../Markdown';
import type { BacktestRow } from '../../../types/financial-model-v2';

function money(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Toned by SIZE of error, not direction — over and under are both misses. */
function errorTone(pctError: number | null): string {
  if (pctError === null) return 'text-gray-400';
  const magnitude = Math.abs(pctError);
  if (magnitude < 0.05) return 'text-green-700';
  if (magnitude < 0.15) return 'text-amber-700';
  return 'text-red-700';
}

function RowGroup({ category, rows }: { category: string; rows: BacktestRow[] }) {
  return (
    <>
      <tr className="bg-gray-50">
        <td
          colSpan={4}
          className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500"
        >
          {category}
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.label} className="border-t border-gray-100">
          <td className="px-4 py-2 text-sm text-gray-800">{row.label}</td>
          <td className="px-4 py-2 text-right text-sm tabular-nums text-gray-900">
            {money(row.actual)}
          </td>
          <td className="px-4 py-2 text-right text-sm tabular-nums text-gray-900">
            {money(row.predicted)}
          </td>
          <td
            className={`px-4 py-2 text-right text-sm font-medium tabular-nums ${errorTone(
              row.pctError,
            )}`}
          >
            {row.pctError === null ? '—' : `${(row.pctError * 100).toFixed(1)}%`}
          </td>
        </tr>
      ))}
    </>
  );
}

export function BacktestViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const years = useFinancialModelV2Store((s) => s.backtestYears);
  const result = useFinancialModelV2Store((s) => s.backtest);
  const isRunning = useFinancialModelV2Store((s) => s.isBacktesting);
  const error = useFinancialModelV2Store((s) => s.backtestError);
  const loadYears = useFinancialModelV2Store((s) => s.loadBacktestYears);
  const runBacktest = useFinancialModelV2Store((s) => s.runBacktest);
  const analyseBacktest = useFinancialModelV2Store((s) => s.analyseBacktest);
  const analysis = useFinancialModelV2Store((s) => s.backtestAnalysis);
  const isAnalysing = useFinancialModelV2Store((s) => s.isAnalysingBacktest);
  const analysisError = useFinancialModelV2Store((s) => s.backtestAnalysisError);

  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    void loadYears();
  }, [loadYears, model?.id]);

  if (!model) return null;

  const year = selected ?? years[years.length - 1] ?? null;

  if (years.length === 0) {
    return (
      <div className="h-full overflow-auto p-6">
        <h2 className="mb-3 text-lg font-bold text-gray-800">Backtest</h2>
        <div className="max-w-2xl rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          This model has no year that can be held out. A backtest needs a
          historical year with actuals <em>and</em> an earlier historical year
          to seed the prediction from, so at least two are required. Set them
          under Settings, or import a workbook that carries them.
        </div>

        {/* Still offered here. Reconciliation needs reported balances, not a
            year to hold out, so it answers something on a model that cannot
            be backtested at all. */}
        <ReconciliationSection />
      </div>
    );
  }

  const byCategory = new Map<string, BacktestRow[]>();
  for (const row of result?.rows ?? []) {
    const group = byCategory.get(row.category) ?? [];
    group.push(row);
    byCategory.set(row.category, group);
  }

  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-1 text-lg font-bold text-gray-800">Backtest</h2>
      <p className="mb-4 max-w-3xl text-sm text-gray-500">
        Hold out a year the model already has actuals for, re-run it as a
        forecast, and compare. It measures whether the forecast mechanics
        reproduce a year we know the answer to.
      </p>

      <div className="mb-5 flex items-end gap-2">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Year to hold out
          </span>
          <select
            value={year ?? ''}
            onChange={(e) => setSelected(Number(e.target.value))}
            className="mt-1 block rounded border border-gray-300 px-3 py-1.5 text-sm"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={isRunning || year === null}
          onClick={() => year !== null && void runBacktest(year)}
          className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {isRunning ? 'Running…' : 'Run backtest'}
        </button>
      </div>

      {error && (
        <div className="mb-4 max-w-3xl rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {result && (
        <div className="max-w-4xl space-y-4">
          {/* Caveats first — see the file comment. */}
          <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <ul className="list-disc space-y-1 pl-4">
              {result.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {result.mapeByCategory.map((entry) => (
              <div
                key={entry.category}
                className="rounded-lg border border-gray-200 bg-white p-4"
              >
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  {entry.category} mean error
                </div>
                <div className="mt-1 text-xl font-semibold text-gray-900">
                  {(entry.mape * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500">
                  across {entry.count} line{entry.count === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Line
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actual {result.holdoutYear}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Predicted
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...byCategory.entries()].map(([category, rows]) => (
                  <RowGroup key={category} category={category} rows={rows} />
                ))}
              </tbody>
            </table>
          </div>

          {/* The variance table says the forecast missed; this says which
              assumption to look at, which is the only part a reader can act
              on. Asked for rather than run automatically - it costs an API
              call and most runs do not need explaining. */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">
                  What explains the variance
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Reads the table above and names the likely cause
                </p>
              </div>
              <button
                type="button"
                disabled={isAnalysing || year === null}
                onClick={() => year !== null && void analyseBacktest(year)}
                className="shrink-0 rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {isAnalysing
                  ? 'Analysing…'
                  : analysis
                    ? 'Regenerate'
                    : 'Explain these variances'}
              </button>
            </div>
            <div className="px-4 py-3">
              {analysisError && (
                <p className="text-sm text-red-700">{analysisError}</p>
              )}
              {!analysisError && analysis && <Markdown>{analysis}</Markdown>}
              {!analysisError && !analysis && !isAnalysing && (
                <p className="text-sm text-gray-500">
                  The variances above are arithmetic and stand on their own.
                  Ask for an explanation to get the likely cause and a
                  directional recalibration.
                </p>
              )}
              {isAnalysing && (
                <p className="text-sm text-gray-500">
                  Reading the variance table…
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <ReconciliationSection />
    </div>
  );
}

/**
 * The engine's assumptions, checked against the figures that were reported.
 *
 * Separate from the backtest above and not dependent on it. The backtest asks
 * "can the forecast mechanics reproduce a year"; this asks "do the
 * assumptions reproduce the balances the company actually reported". The
 * second is answerable on any model that carries reported balances, including
 * one with too few years to hold one out.
 */
function ReconciliationSection() {
  const result = useFinancialModelV2Store((s) => s.reconciliation);
  const isRunning = useFinancialModelV2Store((s) => s.isReconciling);
  const error = useFinancialModelV2Store((s) => s.reconciliationError);
  const run = useFinancialModelV2Store((s) => s.runReconciliation);

  const nothingToReconcile = result != null && result.perYear.length === 0;

  return (
    <div className="mt-8 max-w-4xl">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">
            Historical reconciliation
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Removes the reported closing balances, lets the engine derive them
            from its own assumptions, and compares. A difference is a statement
            about the assumption in the driver column, not about the arithmetic.
          </p>
        </div>
        <button
          type="button"
          disabled={isRunning}
          onClick={() => void run()}
          className="shrink-0 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {isRunning ? 'Reconciling…' : result ? 'Run again' : 'Reconcile'}
        </button>
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* An empty result is an answer, not a failure, and it carries its own
          explanation. Rendering it as a blank panel would look like a bug -
          which is exactly what the comparable multiples did. */}
      {nothingToReconcile && (
        <div className="rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          {result.notes.map((note) => (
            <p key={note} className="mb-1 last:mb-0">
              {note}
            </p>
          ))}
        </div>
      )}

      {result && result.perYear.length > 0 && (
        <div className="space-y-4">
          <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <ul className="list-disc space-y-1 pl-4">
              {result.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>

          {result.perYear.map((year) => (
            <div
              key={year.year}
              className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
            >
              <h3 className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                {year.year}
              </h3>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500">
                    <th scope="col" className="px-4 py-1.5 text-left font-medium">Line</th>
                    <th scope="col" className="px-4 py-1.5 text-right font-medium">Reported</th>
                    <th scope="col" className="px-4 py-1.5 text-right font-medium">Derived</th>
                    <th scope="col" className="px-4 py-1.5 text-right font-medium">Difference</th>
                    <th scope="col" className="px-4 py-1.5 text-left font-medium">Driver</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {year.rows.map((row) => (
                    <tr key={row.label}>
                      <td className="px-4 py-2 text-gray-600">{row.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                        {money(row.reported)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                        {money(row.derived)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right tabular-nums ${errorTone(row.pctError)}`}
                      >
                        {row.pctError === null
                          ? money(row.delta)
                          : `${(row.pctError * 100).toFixed(1)}%`}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">{row.driver}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
